import { PaymentResult } from '@shared/classes/payment-result.class';
import { Injectable } from '@angular/core';
import { PAYMENT_PROVIDERS } from '@shared/constants/payment-providers.const';
import {
  CommonRequestOptions,
  LastTransactionEntry,
  MakePaymentOptions,
  MakePaymentRequestOptions,
  MakePaymentResponse,
  PaymentType,
  TransactionStatusRequestOptions,
} from '../adyen-types';
import { AdyenPaymentApi } from './api/adyen-payment-api.service';
import { PaymentProcessingService } from '@shared/components/payment-processing/payment-processing.service';
import { PaymentProcessingActions } from '@shared/components/payment-processing/payment-processing-actions.enum';
import { AdyenUtils } from '@core/services/system/adyen/adyen-utils';
import { LogService } from '../../logger/log.service';
import { AdyenPaymentErrors } from '../costants';
import { TranslateService } from '@ngx-translate/core';
import { ErrorTranslationHelper } from '@shared/classes';
import { DefaultPaymentMethods } from '@shared/constants';
import { SetTimeoutUtil } from '@core/services/utils/settimeout.utils';
import { UuidService } from '@core/services/utils/uuid.utils';
import { AdyenPaymentError } from '@core/services/system/adyen/classes';
import { AdyenErrorPaymentActions } from './../interfaces';
import { AdyenSoftPosService } from '@core/services/system/adyen/services/adyen-soft-pos.service';
import { AdyenLastTransactionMapService } from '@core/services/system/adyen/services/adyen-last-transaction-map.service';
import { ErrorCondition } from '@core/services/system/adyen/enums';

type MakeTransactionOptions = { type: PaymentType } & MakePaymentOptions;

@Injectable()
export class AdyenPaymentService {
  private readonly errorTranslationHelper = new ErrorTranslationHelper();
  private readonly logger = this.logService.createLogger('AdyenPaymentService');

  constructor(
    public UuidService: UuidService,
    private readonly adyenPaymentApi: AdyenPaymentApi,
    private readonly paymentProcessingService: PaymentProcessingService,
    private readonly adyenSoftPosService: AdyenSoftPosService,
    private readonly adyenLastTransactionMapService: AdyenLastTransactionMapService,
    private readonly utils: AdyenUtils,
    private readonly logService: LogService,
    private readonly setTimeoutUtil: SetTimeoutUtil,
    private readonly translateService: TranslateService
  ) {
    this.setupErrorMessages();
  }

  private setupErrorMessages() {
    this.errorTranslationHelper.setupMessages([
      [AdyenPaymentErrors.userCancel, 'terminal_transaction_error_user_cancel'],
      [AdyenPaymentErrors.provideTerminalInfo, 'settings_provide_terminal_info'],
      [AdyenPaymentErrors.currencyNotSupported, 'terminal_transaction_error_wrong_currency'],
      [AdyenPaymentErrors.refundNotEnabled, 'payment_method_does_not_support_refund'],
      [AdyenPaymentErrors.notEnoughBalance, 'terminal_transaction_error_not_enough_balance'],
      [AdyenPaymentErrors.NoAcquirerFound, 'terminal_transaction_payment_method_unavailable'],
      [AdyenPaymentErrors.declinedOnline, 'terminal_transaction_error_declined'],
      [AdyenPaymentErrors.notPresentCard, 'terminal_transaction_error_not_present_card'],
      [AdyenPaymentErrors.timeoutWaitingResponse, 'connection_timeout'],
      [AdyenPaymentErrors.applicationTimeout, 'connection_timeout'],
      [AdyenPaymentErrors.cardRemoved, 'terminal_transaction_error_user_cancel'],
      [AdyenPaymentErrors.failedToConnect, 'terminal_error_connect_fail'],
      [AdyenPaymentErrors.statusInProgress, 'terminal_error_connection_fail'],
      [AdyenPaymentErrors.connectFailedToPaymentHost, 'terminal_error_connect_fail'],
      [AdyenPaymentErrors.connectFailedToServer, 'terminal_error_connect_fail'],
      [AdyenPaymentErrors.requestTimeout, 'connection_timeout'],
      [AdyenPaymentErrors.timeout, 'terminal_error_connect_fail'],
      [AdyenPaymentErrors.wrongPin, 'terminal_error_wrong_pin'],
      [AdyenPaymentErrors.cardExpired, 'terminal_error_card_expired'],
      [AdyenPaymentErrors.missingAuthentication, 'Missing Authentication']
    ]);
  }

  makePayment(options: MakePaymentOptions): Promise<PaymentResult> {
    const { isPhysicalDevice } = options;
    return isPhysicalDevice ?
      this.prepareTransaction({ type: 'payment', ...options }) :
      this.makeSoftPosPayment(options);
  }

  makeRefund(options: MakePaymentOptions): Promise<PaymentResult> {
    const { isPhysicalDevice } = options;
    return isPhysicalDevice ? this.prepareTransaction({ type: 'refund', ...options }) : this.makeSoftPosRefund(options);
  }

  checkLostTransaction(entry: LastTransactionEntry): Promise<PaymentResult> {
    const commonOptions: CommonRequestOptions = this.utils.createCommonRequestOptions();
    return this.checkTransactionStatus(
      {
        ...commonOptions,
        saleID: entry.saleID,
        serviceID: entry.serviceID,
      }
    );
  }

  private prepareTransaction(options: MakeTransactionOptions) {
    return new Promise<PaymentResult>((resolve, reject) => {
      const { invoiceUuid } = options;
      let transactionError: Error = null;
      const requestOptions: MakePaymentRequestOptions = this.getPaymentRequestOptions(options);
      this.paymentProcessingService.init(
        () => {
          // retry user action
          return this.isOnline()
            .then(() => this.retryTransaction(options, invoiceUuid))
            .then(resolve)
            .catch((error) => (transactionError = error));
        },
        () => reject(transactionError), // close user action
        () => {
          // check user action
          return this.checkTransactionStatus(requestOptions, invoiceUuid)
            .then(resolve)
            .catch((error) => (transactionError = error));
        },
        options.type
      );
      this.paymentProcessingService.dispatchAction(PaymentProcessingActions.processing);
      this.isOnline()
        .then(() => this.makeTransaction(requestOptions, invoiceUuid))
        .then(resolve)
        .catch((error) => (transactionError = error));
    });
  }

  makeSoftPosRefund(options: MakePaymentOptions) {
    return this.makeSoftPosTransaction(options, 'refund');
  }

  makeSoftPosPayment(options: MakePaymentOptions) {
    return this.makeSoftPosTransaction(options, 'payment');
  }

  makeSoftPosTransaction(options: MakePaymentOptions, type: 'payment' | 'refund') {
    return new Promise<PaymentResult>((resolve, reject) => {
      const { invoiceUuid } = options;
      let transactionError: Error = null;
      const optionsTransactions: MakeTransactionOptions = { type, ...options };
      const requestOptions: MakePaymentRequestOptions = this.getPaymentRequestOptions(optionsTransactions);
      const saleToPOIRequest = this.adyenPaymentApi.generateSaleToPOIRequest(requestOptions, 'softpos');
      this.paymentProcessingService.init(
        () =>
          this.retryTransactionSoftPos(optionsTransactions, invoiceUuid) // retry user action
            .then((data: PaymentResult): PaymentResult => data)
            .then(resolve)
            .catch((error) => (transactionError = error)),
        () => reject(transactionError), // close user action
        () => {
          return this.checkTransactionStatus(requestOptions, invoiceUuid)
            .then(resolve)
            .catch((error) => (transactionError = error));
        },
        optionsTransactions.type
      );
      this.paymentProcessingService.dispatchAction(PaymentProcessingActions.processing);
      this.transactionSoftPosProcess(saleToPOIRequest, invoiceUuid)
        .then((data: PaymentResult): PaymentResult => data)
        .then(resolve)
        .catch(error => (transactionError = error));
    });
  }

  private async finishTransaction(paymentResponse: MakePaymentResponse, invoiceUuid?: string) {
    const payment: PAYMENT_PROVIDERS = PAYMENT_PROVIDERS.PAYMASH_PAY;
    const paymentResult = new PaymentResult(payment);
    paymentResult.setPaymentResultData(paymentResponse);
    if (invoiceUuid) {
      this.adyenLastTransactionMapService.removeTransaction(invoiceUuid);
    }
    return paymentResult;
  }

  private makeTransaction(options: MakePaymentRequestOptions, invoiceUuid: string): Promise<unknown> {
    const { saleID, serviceID } = options;
    this.adyenLastTransactionMapService.saveTransaction(invoiceUuid, saleID, serviceID);
    return this.adyenPaymentApi
      .makePayment(options)
      .then((paymentResponse: MakePaymentResponse): Promise<PaymentResult> => this.finishTransaction(paymentResponse, invoiceUuid))
      .catch((error: AdyenPaymentError) => {
        this.processTransactionErrors(error, invoiceUuid);
      });
  }

  private transactionSoftPosProcess(saleToPOIRequest, invoiceUuid: string) {
    const { SaleID, ServiceID } = saleToPOIRequest?.SaleToPOIRequest?.MessageHeader ?? {};
    this.adyenLastTransactionMapService.saveTransaction(invoiceUuid, SaleID, ServiceID);
    return this.adyenSoftPosService
      .createPaymentToSoftPos(saleToPOIRequest)
      .then((paymentResponse: MakePaymentResponse): Promise<PaymentResult> => this.finishTransaction(paymentResponse, invoiceUuid))
      .catch((error: AdyenPaymentError) => {
        this.processTransactionErrors(error, invoiceUuid);
      });
  }

  private processTransactionErrors(error: AdyenPaymentError, invoiceUuid?: string) {
    if (error.isLegalError) {
      const message: string = error?.refusalReason + ' ' + error?.errorCondition + ' ' + error?.message;
      // Expected user-action error (cancel / insufficient funds / ...) — logged as an error for
      // diagnostics but kept out of Sentry by the isLegalError filter on SentryErrorReporter.
      this.logger.error(error, message);
      if (invoiceUuid) {
        this.adyenLastTransactionMapService.removeTransaction(invoiceUuid);
      }
    } else {
      this.logger.error(error, 'Logger make transaction: ');
    }

    const { action, message: displayMessage, additionalButtonConfig } = this.getActionsAfterError(error);

    this.paymentProcessingService.dispatchAction(action, {
      message: this.translateService.instant(displayMessage),
      ...additionalButtonConfig
    });

    // Preserve isLegalError (and context) through the re-throw so upstream logging + the Sentry filter
    // can still tell this was an expected user-action error — the default ctor would reset it to false.
    throw new AdyenPaymentError(error.message, error.errorCondition, error.refusalReason, error.keyError, error.isLegalError);
  }

  private getPaymentRequestOptions(options: MakeTransactionOptions): MakePaymentRequestOptions {
    const { currency, amount, paymentUuid, type, paymentMethod } = options;
    const requestOptions: MakePaymentRequestOptions = {
      ...this.utils.createCommonRequestOptions(),
      currency,
      amount,
      saleTransaction: {
        transactionID: paymentUuid, // "27908" -- your reference to identify a payment. We recommend using a unique value per payment. In your Customer Area and Adyen reports, this will show as the merchant reference for the transaction.
        timeStamp: new Date().toISOString(), // "2019-03-07T10:11:04+00:00" -- date and time of the request in UTC format.
      },
      type,
      allowedPaymentBrand: paymentMethod === DefaultPaymentMethods.TWINT ? 'twint_pos' : undefined,
    };
    return requestOptions;
  }

  private checkTransactionStatus(options: CommonRequestOptions, invoiceUuid?: string): Promise<PaymentResult> {
    this.paymentProcessingService.dispatchAction(PaymentProcessingActions.processing, {
      checkButtonOff: true
    });
    const requestOptions = this.getTransactionStatusRequestOptions(options);
    return this.adyenPaymentApi
      .checkTransactionStatus(requestOptions)
      .then((paymentResponse) => this.finishTransaction(paymentResponse, invoiceUuid))
      .catch((error) => {
        this.logger.error(error, 'checkTransactionStatus');

        const { action, message, additionalButtonConfig } = this.getActionsAfterError(error);

        this.paymentProcessingService.dispatchAction(action, {
          message: this.translateService.instant(message),
          ...additionalButtonConfig,
        });
        throw error;
      });
  }

  private retryTransaction(options: MakeTransactionOptions, invoiceUuid: string) {
    this.paymentProcessingService.dispatchAction(PaymentProcessingActions.processing);
    const requestOptions = this.getTransactionRetryRequestOptions(options);
    return this.makeTransaction(requestOptions, invoiceUuid);
  }

  private retryTransactionSoftPos(options: MakeTransactionOptions, invoiceUuid: string) {
    this.paymentProcessingService.dispatchAction(PaymentProcessingActions.processing);
    const requestOptions: MakePaymentRequestOptions = this.getTransactionRetryRequestOptions(options);
    const saleToPOIRequest = this.adyenPaymentApi.generateSaleToPOIRequest(requestOptions, 'softpos');
    return this.transactionSoftPosProcess(saleToPOIRequest, invoiceUuid);
  }

  private getTransactionStatusRequestOptions(options: CommonRequestOptions): TransactionStatusRequestOptions {
    return {
      ...this.utils.createCommonRequestOptions(),
      transactionSaleID: options.saleID,
      transactionServiceID: options.serviceID,
    };
  }

  private getTransactionRetryRequestOptions(options: MakeTransactionOptions): MakePaymentRequestOptions {
    const { currency, amount, type, paymentMethod } = options;
    const requestOptions: MakePaymentRequestOptions = {
      ...this.utils.createCommonRequestOptions(),
      currency,
      amount,
      saleTransaction: {
        transactionID: this.UuidService.generate(), // "27908" -- your reference to identify a payment. We recommend using a unique value per payment. In your Customer Area and Adyen reports, this will show as the merchant reference for the transaction.
        timeStamp: new Date().toISOString(), // "2019-03-07T10:11:04+00:00" -- date and time of the request in UTC format.
      },
      type,
      allowedPaymentBrand: paymentMethod === DefaultPaymentMethods.TWINT ? 'twint_pos' : undefined,
    };
    return requestOptions;
  }

  private async isOnline() {
    await this.setTimeoutUtil.waitTimeAndDo(1);
    const requestOptions = this.utils.createCommonRequestOptions();
    return this.adyenPaymentApi.diagnostic(requestOptions).catch((error) => {
      this.logger.error(error, 'diagnostic');
      this.paymentProcessingService.dispatchAction(PaymentProcessingActions.retry, {
        message: this.generateMessageError(PaymentProcessingActions.retry, error),
        retryButtonOff: true,
      });
      throw error;
    });
  }

  private getActionsAfterError(error: AdyenPaymentError): AdyenErrorPaymentActions {
    const { errorCondition } = error;

    const isRetryAction: boolean = this.isNeedToRetry(errorCondition);
    const isWithoutAdditionalButton: boolean = this.isWithOutUserOptions(errorCondition);


    const action = isRetryAction
      ? PaymentProcessingActions.retry
      : PaymentProcessingActions.check;

    const message: string = this.generateMessageError(action, error, errorCondition);

    const additionalButtonConfig: Record<string, boolean> = isRetryAction
      ? { retryButtonOff: isWithoutAdditionalButton }
      : { checkButtonOff: isWithoutAdditionalButton };

    return { action, message, additionalButtonConfig };
  }

  // TODO isTimeOutError() may be deprecated functionality !
  // @ts-ignore
  private isTimeOutError(translateKey: string): boolean {
    const timeoutErrors: string[] = [
      AdyenPaymentErrors.timeoutWaitingResponse,
      AdyenPaymentErrors.applicationTimeout,
      AdyenPaymentErrors.requestTimeout,
    ];

    return timeoutErrors
      .map((timeoutError) => this.errorTranslationHelper.getTranslationKey(timeoutError))
      .some((timeoutError) => timeoutError === translateKey);
  }

  private isNeedToRetry(errorCondition: ErrorCondition): boolean {
    const retryCases: ErrorCondition[] = [
      ErrorCondition.Refusal,
      ErrorCondition.InProgress,
      ErrorCondition.NotFound,
      ErrorCondition.InvalidCard,
      ErrorCondition.WrongPIN,
      ErrorCondition.NotAllowed
    ];

    return retryCases.includes(errorCondition);
  }

  private isWithOutUserOptions(errorCondition: ErrorCondition): boolean {
    const emptyOptionsCases: ErrorCondition[] = [
      ErrorCondition.Aborted,
      ErrorCondition.Busy,
      ErrorCondition.Cancel,
      ErrorCondition.DeviceOut,
      ErrorCondition.UnavailableService,
      ErrorCondition.DeviceNotSafe,
      ErrorCondition.SecurityBlock,
      ErrorCondition.SoftPosError
    ];

    return emptyOptionsCases.includes(errorCondition);
  }

  private isShowMessageWithoutTranslate(errorCondition: ErrorCondition): boolean {
    const setShowRawMessage: ErrorCondition[] = [
      ErrorCondition.UnavailableService,
      ErrorCondition.DeviceNotSafe,
      ErrorCondition.SecurityBlock,
      ErrorCondition.SoftPosError
    ]

    return setShowRawMessage.includes(errorCondition);
  }

  private generateMessageError(action: PaymentProcessingActions, error: AdyenPaymentError, errorCondition?: ErrorCondition): string {

    const isShowMessageWithoutTranslate: boolean = errorCondition ? this.isShowMessageWithoutTranslate(errorCondition) : false;

    const message: string =
      this.errorTranslationHelper.getTranslationKey(error?.keyError)
      || this.errorTranslationHelper.getTranslationKey(error?.message)
      || this.errorTranslationHelper.getTranslationKey(error?.refusalReason)
      || (isShowMessageWithoutTranslate ? this.errorTranslationHelper.threatModelChecker(error) : 'adyen_payment_process_common_error_msg');

    const isWithActionButton: boolean = !this.isWithOutUserOptions(error.errorCondition);

    return (action === PaymentProcessingActions.check && isWithActionButton) ? 'transaction_check_status_message' : message;
  }
}
